import { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { closeModal } from 'state/modal/modalSlice';
import { addNewElement, editElement, removeElement, editElementProp } from 'state/fence/fenceSlice';
import { getCurrentFenceGroup, getLargestElementId } from 'state/fence/fenceSelector';
import { editNewElementProps } from 'state/newElement/newElementSlice';
import { currentBrick } from 'helpers/globals';
import { widthInCm, widthInBrick, getItemById, getItemIndexById } from 'helpers/utils';
import ModalBase from 'components/common/ModalBase/ModalBase';
import FormLabel from 'components/common/FormLabel/FormLabel';
import FormNumber from 'components/common/FormNumber/FormNumber';
import FormCheck from 'components/common/FormCheck/FormCheck';
import ButtonBase from 'components/common/ButtonBase/ButtonBase';
import AccordionBase from 'components/common/AccordionBase/AccordionBase';
import AccordionItem from 'components/common/AccordionBase/AccordionItem';
import style from './FenceCanvasModals.module.scss';

let addedTempElement = false;

const ModalAddElement = () => {
  const [currentElementId, setCurrentElementId] = useState(null);
  const dispatch = useDispatch();
  const currentFenceGroup = useSelector(getCurrentFenceGroup);
  const largestElementId = useSelector(getLargestElementId);
  const newElement = useSelector((state) => state.newElement);
  const modal = useSelector((state) => state.modal);
  const currentIndex = getItemIndexById(currentFenceGroup.elements, currentElementId);
  const currentElement = getItemById(currentFenceGroup.elements, currentElementId);

  const getDoorTypeValue = useMemo(() => {
    if (currentElement) {
      return currentElement.type;
    }

    return newElement.door.type;
  }, [currentElement, newElement.door.type]);

  const onShowModal = () => {
    if (!addedTempElement) {
      const name = newElement.activeName;
      const newId = largestElementId + 1;

      setCurrentElementId(newId);

      dispatch(
        addNewElement({
          insertAfterId: modal.props.id ? modal.props.id : null,
          element: {
            id: newId,
            name,
            ...newElement[name],
          },
        })
      );
    }

    addedTempElement = true;
  };

  const onChangeActiveItemHandler = (name) => {
    if (currentElementId) {
      dispatch(
        editElement({
          index: currentIndex,
          element: {
            id: currentElementId,
            name,
            ...newElement[name],
          },
        })
      );
    }
  };

  const editNewElementPropsHandler = (prop, value) => {
    if (!currentElement || currentElement[prop] === value) return;

    dispatch(editElementProp({ index: currentIndex, prop, value }));
  };

  const onChangeBrickCountVertically = (value) => {
    editNewElementPropsHandler('brickCountVertically', value);
  };

  const onChangeBrickCountHorizontally = (value) => {
    editNewElementPropsHandler('brickCountHorizontally', widthInBrick(value));
  };

  const onChangeDoorCmHorizontally = (value) => {
    editNewElementPropsHandler('cmHorizontally', value);
  };

  const onChangeDoorType = (value) => {
    editNewElementPropsHandler('type', value);
  };

  const onAddNewElement = () => {
    if (currentElement) {
      const { id, name, ...element } = currentElement;
      let activeName;

      switch (name) {
        case 'column':
          activeName = 'fundament';
          break;
        case ('fundament', 'door'):
          activeName = 'column';
          break;
        default:
          activeName = 'column';
      }

      dispatch(
        editNewElementProps({
          activeName,
          [name]: {
            ...element,
          },
        })
      );
    }

    dispatch(closeModal());
    addedTempElement = false;
  };

  const onResetNewElement = () => {
    dispatch(closeModal());
    dispatch(removeElement(currentIndex));
    addedTempElement = false;
  };

  return (
    <ModalBase name="add-element" onShow={onShowModal}>
      <AccordionBase className={style.modalAccordion} onChangeActiveItem={onChangeActiveItemHandler}>
        <AccordionItem active={newElement.activeName === 'column'} id="column" title="Oszlop hozzáadása">
          <div className="row align-items-center mb-xs">
            <div className="col-6">
              <FormLabel>Magasság:</FormLabel>
            </div>
            <div className="col-6">
              <FormNumber
                defaultValue={newElement.column.brickCountVertically}
                onChange={onChangeBrickCountVertically}
                step="1"
                min="1"
                max="15"
                unit="sor"
              />
            </div>
          </div>

          <div className="row align-items-center mb-md">
            <div className="col-6">
              <FormLabel>Szélesség:</FormLabel>
            </div>
            <div className="col-6">
              <FormNumber
                defaultValue={widthInCm(newElement.column.brickCountHorizontally)}
                onChange={onChangeBrickCountHorizontally}
                step={currentBrick.halfBrick.width}
                min={currentBrick.halfBrick.width}
                max={currentBrick.halfBrick.width * 40}
                unit="cm"
              />
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <ButtonBase color="primary" className="w-full" onClick={onAddNewElement}>
                Hozzáadás
              </ButtonBase>
            </div>

            <div className="col-6">
              <ButtonBase className="w-full" onClick={onResetNewElement}>
                Mégsem
              </ButtonBase>
            </div>
          </div>
        </AccordionItem>
        <AccordionItem active={newElement.activeName === 'fundament'} id="fundament" title="Lábazat hozzáadása">
          <div className="row align-items-center mb-xs">
            <div className="col-6">
              <FormLabel>Magasság:</FormLabel>
            </div>
            <div className="col-6">
              <FormNumber
                defaultValue={newElement.fundament.brickCountVertically}
                onChange={onChangeBrickCountVertically}
                step="1"
                min="2"
                max="15"
                unit="sor"
              />
            </div>
          </div>

          <div className="row align-items-center mb-md">
            <div className="col-6">
              <FormLabel>Szélesség:</FormLabel>
            </div>
            <div className="col-6">
              <FormNumber
                defaultValue={widthInCm(newElement.fundament.brickCountHorizontally)}
                onChange={onChangeBrickCountHorizontally}
                step={currentBrick.halfBrick.width}
                min={currentBrick.halfBrick.width}
                max={currentBrick.halfBrick.width * 40}
                unit="cm"
              />
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <ButtonBase color="primary" className="w-full" onClick={onAddNewElement}>
                Hozzáadás
              </ButtonBase>
            </div>

            <div className="col-6">
              <ButtonBase className="w-full" onClick={onResetNewElement}>
                Mégsem
              </ButtonBase>
            </div>
          </div>
        </AccordionItem>
        <AccordionItem active={newElement.activeName === 'door'} id="door" title="Kis-/nagykapu hozzáadása">
          <div className="row align-items-center mb-xs">
            <div className="col-6">
              <FormLabel>Szélesség:</FormLabel>
            </div>
            <div className="col-6">
              <FormNumber
                defaultValue={newElement.door.cmHorizontally}
                onChange={onChangeDoorCmHorizontally}
                step="1"
                min="30"
                max="500"
                unit="cm"
                typeable
              />
            </div>
          </div>

          <div className="row align-items-center mb-md">
            <div className="col-4">
              <FormLabel>Kapu nyitás:</FormLabel>
            </div>
            <div className="col-8">
              <div className="row">
                <div className="col-6">
                  <FormCheck mode="box"
                    type="radio"
                    name="door-type"
                    groupValue={getDoorTypeValue}
                    value="simple"
                    onChange={onChangeDoorType}
                  >
                    Egyszárnyú
                  </FormCheck>
                </div>
                <div className="col-6">
                  <FormCheck mode="box"
                    type="radio"
                    name="door-type"
                    groupValue={getDoorTypeValue}
                    value="double"
                    onChange={onChangeDoorType}
                  >
                    Kétszárnyú
                  </FormCheck>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <ButtonBase color="primary" className="w-full" onClick={onAddNewElement}>
                Hozzáadás
              </ButtonBase>
            </div>

            <div className="col-6">
              <ButtonBase className="w-full" onClick={onResetNewElement}>
                Mégsem
              </ButtonBase>
            </div>
          </div>
        </AccordionItem>
      </AccordionBase>
    </ModalBase>
  );
};

export default ModalAddElement;
